import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CalendarHeart, CheckCheck, FileText, HeartPulse, MessageSquare, Receipt, Sparkles } from "lucide-react";
import { useFamilyAuth } from "../../context/FamilyAuthContext";
import { useNotificationList, useUnreadCount } from "./familyNotifications";
import { familyRichiestaWhen } from "../../utils/familyRichieste";

export const notificationIcons = {
  aggiornamento: Sparkles, momento: CalendarHeart, documento: FileText, richiesta: MessageSquare, fattura: Receipt, percorso: HeartPulse,
};

export function NotificationRow({ item, onOpen, onRead, itemRef }) {
  const Icon = notificationIcons[item.tipo] || Bell;
  return (
    <li className={`flex items-start gap-2 rounded-xl p-2 ${item.letta ? "" : "bg-[#EDE9F6]"}`} data-unread={item.letta ? "false" : "true"}>
      <Link
        to={item.link}
        ref={itemRef}
        data-notif-item
        onClick={() => onOpen(item)}
        className="flex min-w-0 flex-1 items-start gap-3 rounded-lg p-1 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#6e48b5]"
      >
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-[#6e48b5]"><Icon className="h-4 w-4" /></span>
        <span className="min-w-0">
          <span className={`block text-sm text-[#304762] ${item.letta ? "font-medium" : "font-extrabold"}`}>
            {item.letta ? null : <span className="sr-only">Non letta: </span>}{item.titolo}
          </span>
          {item.testo ? <span className="block truncate text-xs text-[#60758f]">{item.testo}</span> : null}
          <span className="mt-0.5 block text-[11px] text-[#8a9bb0]">{familyRichiestaWhen(item.created_at)}</span>
        </span>
      </Link>
      {item.letta ? null : (
        <button
          type="button"
          onClick={() => onRead(item.id)}
          className="mt-1 shrink-0 rounded-lg px-2 py-1 text-[11px] font-bold text-[#6e48b5] hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#6e48b5]"
          aria-label={`Segna come letta: ${item.titolo}`}
        >
          Segna come letta
        </button>
      )}
    </li>
  );
}

export function NotificationStates({ status, error, items, onRetry, emptyText = "Nessuna notifica recente." }) {
  if (status === "loading" || status === "idle") return <p className="p-4 text-center text-sm text-[#72839a]" role="status">Caricamento delle notifiche…</p>;
  if (status === "error") {
    return (
      <div className="p-4 text-center" role="alert">
        <p className="text-sm text-red-700">{error}</p>
        <button type="button" onClick={onRetry} className="mt-2 rounded-lg bg-[#EDE9F6] px-3 py-1.5 text-xs font-bold text-[#6e48b5]">Riprova</button>
      </div>
    );
  }
  if (!items.length) return <p className="p-6 text-center text-sm text-[#72839a]">{emptyText}</p>;
  return null;
}

export default function FamilyNotificationBell() {
  const { api, activeOspiteId } = useFamilyAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const { count } = useUnreadCount(api, activeOspiteId, true);
  const list = useNotificationList(api, activeOspiteId, { pageSize: 8, active: open });
  const [announce, setAnnounce] = useState("");
  const previous = useRef(null);

  // annuncio per gli screen reader solo quando il numero cambia davvero (non al primo caricamento)
  useEffect(() => {
    if (count === null) return;
    if (previous.current !== null && previous.current !== count) {
      setAnnounce(count === 0 ? "Nessuna notifica da leggere" : `${count} ${count === 1 ? "notifica da leggere" : "notifiche da leggere"}`);
    }
    previous.current = count;
  }, [count]);

  useEffect(() => { previous.current = null; setAnnounce(""); }, [activeOspiteId]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    const onKey = (event) => {
      if (event.key === "Escape") { setOpen(false); buttonRef.current?.focus(); }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onPointer); document.removeEventListener("keydown", onKey); };
  }, [open]);

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  const onListKey = (event) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const nodes = [...panelRef.current.querySelectorAll("[data-notif-item]")];
    if (!nodes.length) return;
    event.preventDefault();
    const index = nodes.indexOf(document.activeElement);
    const next = event.key === "ArrowDown" ? (index + 1) % nodes.length : (index - 1 + nodes.length) % nodes.length;
    nodes[index === -1 ? 0 : next].focus();
  };

  const unread = count ?? 0;
  const label = unread ? `Notifiche, ${unread} da leggere` : "Notifiche";
  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Notifiche"
        data-testid="family-bell"
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-white"
      >
        <Bell className="h-[18px] w-[18px]" />
        {unread ? (
          <span aria-hidden="true" data-testid="family-bell-badge" className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </button>
      <span role="status" aria-live="polite" className="sr-only">{announce}</span>
      {open ? (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Notifiche"
          tabIndex={-1}
          onKeyDown={onListKey}
          className="family-card fixed inset-x-3 top-16 z-50 max-h-[78dvh] overflow-y-auto p-2 shadow-xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[380px]"
        >
          <div className="flex items-center justify-between gap-2 px-2 py-1.5">
            <h2 className="text-sm font-extrabold text-[#243952]">Notifiche</h2>
            <button
              type="button"
              onClick={list.markAllRead}
              disabled={!list.unread}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-bold text-[#6e48b5] hover:bg-[#EDE9F6] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <CheckCheck className="h-3.5 w-3.5" />Segna tutte come lette
            </button>
          </div>
          <NotificationStates status={list.status} error={list.error} items={list.items} onRetry={list.load} />
          {list.status === "ready" && list.items.length ? (
            <ul className="space-y-1">
              {list.items.map((item) => (
                <NotificationRow
                  key={item.id}
                  item={item}
                  onRead={list.markRead}
                  onOpen={(n) => { list.markRead(n.id); setOpen(false); }}
                />
              ))}
            </ul>
          ) : null}
          <div className="mt-1 border-t border-slate-100 px-2 pt-2 text-center">
            <Link to="/family/notifiche" onClick={() => setOpen(false)} className="text-xs font-bold text-[#6e48b5] hover:underline">Vedi tutte le notifiche</Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
